import { isValidElement, ReactNode } from 'react';

import { KubesoloErrorCode, KubesoloStep, KubesoloTest } from '@api/types.gen';

import { Link } from '@@/Link';
import { CopyButton } from '@@/buttons/CopyButton';

import { formatSize } from '../formatSize';

/** Matches the SSH client's connect timeout on the server. */
const CONNECT_TIMEOUT_SECONDS = 20;
const dotPattern = /\./g;

export interface StepFailure {
  message: ReactNode;
  fix?: ReactNode;
  /** The raw error, kept under Details when the message rewords it */
  details?: string;
}

type Values = Partial<Record<string, string>>;

const REACH_PORTAINER_FIX =
  'Check that the Portainer API URL and tunnel address in the form are reachable from the machine, not only from your browser.';

/** kubesoloctl's failures already say what to do, so they show as it prints them. */
const failures: Record<
  Exclude<KubesoloErrorCode, 'checkFailed'>,
  (values: Values, test: KubesoloTest) => Omit<StepFailure, 'details'>
> = {
  hostNotFound: ({ host }) => ({
    message: (
      <>
        Portainer can&apos;t resolve <code>{host}</code>.
      </>
    ),
    fix: "Enter the machine's IP address, or add the name to the DNS Portainer uses. If Portainer runs in Kubernetes, that is the cluster DNS, which may not forward to internal resolvers.",
  }),
  dnsLookupFailed: ({ host }) => ({
    message: (
      <>
        The DNS server Portainer uses didn&apos;t answer for <code>{host}</code>
        .
      </>
    ),
    fix: "Re-test in a moment, or enter the machine's IP address. If it keeps failing, check the DNS server Portainer uses.",
  }),
  connectionRefused: ({ address }) => ({
    message: (
      <>
        Nothing is listening on <code>{address}</code>.
      </>
    ),
    fix: 'Check that sshd is running, and the port in the Machines list.',
  }),
  hostUnreachable: ({ address }) => ({
    message: (
      <>
        Portainer has no network route to <code>{address}</code>.
      </>
    ),
    fix: 'Check the address, and that the network Portainer runs in can route to the machine.',
  }),
  connectTimeout: ({ address }) => ({
    message: (
      <>
        Portainer can&apos;t reach <code>{address}</code> (no answer in{' '}
        {CONNECT_TIMEOUT_SECONDS}s).
      </>
    ),
    fix: 'Check the machine is on, and that firewalls between Portainer and the machine allow the SSH port.',
  }),
  connectionClosed: () => ({
    message: 'The machine closed the connection before login.',
    fix: (
      <>
        Check the port in the Machines list is the machine&apos;s SSH port. If
        it is, sshd may be limiting new connections (<code>MaxStartups</code>)
        or banning Portainer&apos;s address (fail2ban). Re-test later, or allow
        Portainer&apos;s address.
      </>
    ),
  }),
  noCommonAlgorithm: ({ algorithm }) => ({
    message: (
      <>
        Portainer and the machine&apos;s SSH server have no{' '}
        <code>{algorithm}</code> algorithm in common.
      </>
    ),
    fix: 'Update OpenSSH on the machine.',
  }),
  keyRefused: ({ credential, user }, test) => ({
    message: (
      <>
        The machine refused the <code>{credential}</code> key for user{' '}
        <code>{user}</code>.
      </>
    ),
    fix: (
      <>
        Add the credential&apos;s public key to{' '}
        <code>~{user}/.ssh/authorized_keys</code> on the machine, or pick
        another credential. <EditCredentialLink test={test} />
      </>
    ),
  }),
  credentialUnusable: ({ credential }, test) => ({
    message: (
      <>
        The <code>{credential}</code> credential has no usable private key.
      </>
    ),
    fix: (
      <>
        Edit the credential. <EditCredentialLink test={test} />
      </>
    ),
  }),
  hostKeyChanged: ({ pinned, presented }) => ({
    message: (
      <>
        Host key changed since it was pinned: was <code>{pinned}</code>, now{' '}
        <code>{presented}</code>.
      </>
    ),
    fix: 'A reinstalled machine gets a new key, and so does one pretending to be it. Check the machine, then replace the key.',
  }),
  sudoPasswordRequired: ({ user }) => ({
    message: (
      <>
        <code>{user}</code> can&apos;t run sudo without a password.
      </>
    ),
    fix: (
      <>
        Allow passwordless sudo for that user, or connect as root.{' '}
        <Command
          command={`echo '${user} ALL=(ALL) NOPASSWD: ALL' | sudo tee ${sudoersFile(user)}`}
        />
      </>
    ),
  }),
  sudoRequiresTty: ({ user }) => ({
    message: (
      <>
        sudo on the machine requires a terminal, so <code>{user}</code>{' '}
        can&apos;t use it over SSH.
      </>
    ),
    fix: (
      <>
        Turn off <code>requiretty</code> for that user, or connect as root.{' '}
        <Command
          command={`echo 'Defaults:${user} !requiretty' | sudo tee ${sudoersFile(user)}-requiretty`}
        />
      </>
    ),
  }),
  unsupportedArch: ({ arch }) => ({
    message: (
      <>
        KubeSolo doesn&apos;t run on <code>{arch}</code>.
      </>
    ),
    fix: 'Remove the machine from the list.',
  }),
  kubesoloctlMissing: ({ version, arch }) => ({
    message: (
      <>
        Portainer doesn&apos;t have kubesoloctl <code>{version}</code> for{' '}
        <code>{arch}</code>.
      </>
    ),
    fix: (
      <>
        Choose a KubeSolo version whose release publishes{' '}
        <code>kubesoloctl-linux-{arch}</code>, or remove the machine from the
        list.
      </>
    ),
  }),
  offlineBundleMissing: ({ name, arch, libc }) => ({
    message: (
      <>
        No offline bundle is uploaded for <code>{arch}</code> machines with{' '}
        {libc}.
      </>
    ),
    fix: (
      <>
        Upload <code>{name}</code> under Machines can&apos;t reach the internet.
      </>
    ),
  }),
  bundleNoSpace: ({ user, needed, available }) => {
    // needed already includes room for kubesoloctl beside the bundle
    const shortfallBytes = Number(needed) - Number(available);

    return {
      message:
        "The SSH user's home on the machine has no room for the offline bundle.",
      fix: (
        <>
          Free {formatSize(shortfallBytes)} in <code>~{user}</code>.
        </>
      ),
    };
  },
  kubesoloctlNotRunnable: ({ path }) => ({
    message: (
      <>
        Root can&apos;t run <code>{path}</code> on the machine.
      </>
    ),
    fix: "The SSH user's home may be mounted noexec, or on NFS that maps root to nobody. Connect as a user with a local home, or as root.",
  }),
  noSpace: ({ user }) => ({
    message: "The SSH user's home on the machine is full.",
    fix: (
      <>
        Free about 20 MB in <code>~{user}</code>.
      </>
    ),
  }),
  curlMissing: () => ({
    message: "curl isn't installed on the machine.",
    fix: (
      <>
        Install curl (<code>apt install curl</code>,{' '}
        <code>dnf install curl</code>, <code>apk add curl</code>).
      </>
    ),
  }),
  portainerNotResolved: ({ host }) => ({
    message: (
      <>
        The machine can&apos;t resolve <code>{host}</code>.
      </>
    ),
    fix: REACH_PORTAINER_FIX,
  }),
  portainerProxyFailed: ({ address }) => ({
    message: (
      <>
        The machine&apos;s proxy didn&apos;t pass its request to{' '}
        <code>{address}</code>.
      </>
    ),
    fix: (
      <>
        Add Portainer&apos;s address to <code>no_proxy</code> on the machine, or
        allow it on the proxy.
      </>
    ),
  }),
  portainerUnreachable: ({ address }) => ({
    message: (
      <>
        The machine can&apos;t connect to <code>{address}</code>.
      </>
    ),
    fix: REACH_PORTAINER_FIX,
  }),
};

/** Each failure's cause without the machine, to title the machines that failed the same way. */
const causes: Record<
  Exclude<KubesoloErrorCode, 'checkFailed'>,
  (values: Values) => string
> = {
  hostNotFound: () => "Portainer can't resolve the machine's name",
  dnsLookupFailed: () => "The DNS server Portainer uses didn't answer",
  connectionRefused: () => 'Nothing is listening on the SSH port',
  hostUnreachable: () => 'Portainer has no network route to the machine',
  connectTimeout: () => `No answer in ${CONNECT_TIMEOUT_SECONDS}s`,
  connectionClosed: () => 'The machine closed the connection before login',
  noCommonAlgorithm: ({ algorithm }) => `No ${algorithm} algorithm in common`,
  keyRefused: () => 'The machine refused the SSH key',
  credentialUnusable: () => "The SSH credential can't be used",
  hostKeyChanged: () => 'The host key changed',
  sudoPasswordRequired: () => "sudo can't run without a password",
  sudoRequiresTty: () => 'sudo requires a terminal',
  unsupportedArch: ({ arch }) => `Unsupported architecture ${arch}`,
  kubesoloctlMissing: ({ arch }) =>
    `Portainer doesn't have kubesoloctl for ${arch}`,
  offlineBundleMissing: ({ arch, libc }) =>
    `No offline bundle for ${arch} with ${libc}`,
  bundleNoSpace: () => 'No room for the offline bundle',
  kubesoloctlNotRunnable: () => "Root can't run kubesoloctl",
  noSpace: () => "The SSH user's home is full",
  curlMissing: () => "curl isn't installed",
  portainerNotResolved: ({ host }) => `The machine can't resolve ${host}`,
  portainerProxyFailed: ({ address }) =>
    `The machine's proxy didn't pass its request to ${address}`,
  portainerUnreachable: ({ address }) =>
    `The machine can't connect to ${address}`,
};

export const UNRECOGNISED_CAUSE = 'Unrecognised error';

/** Why a step failed, without naming the machine. kubesoloctl's failures already say it in their own words. */
export function describeCause(step: KubesoloStep) {
  const code = step.error?.code;
  if (!code) {
    return UNRECOGNISED_CAUSE;
  }

  if (code === 'checkFailed') {
    return step.detail ?? '';
  }

  return causes[code](step.error?.values ?? {});
}

/**
 * Explains a failed step in the user's terms, with a fix when there is one.
 * kubesoloctl's own failures, and unknown ones, show the raw detail as the message.
 */
export function explainFailure(
  step: KubesoloStep,
  test: KubesoloTest
): StepFailure {
  const detail = step.detail ?? '';
  const code = step.error?.code;

  if (!code || code === 'checkFailed') {
    return { message: detail };
  }

  return {
    ...failures[code](step.error?.values ?? {}, test),
    details: detail,
  };
}

/** A message or fix as plain text for a prompt, with code in backticks and links left out. */
export function failureText(node: ReactNode): string {
  if (typeof node === 'string' || typeof node === 'number') {
    return String(node);
  }

  if (Array.isArray(node)) {
    return node.map(failureText).join('');
  }

  if (!isValidElement<TextProps>(node) || node.type === EditCredentialLink) {
    return '';
  }

  if (node.type === Command) {
    return `\`${node.props.command ?? ''}\``;
  }

  const text = failureText(node.props.children);
  return node.type === 'code' ? `\`${text}\`` : text;
}

interface TextProps {
  children?: ReactNode;
  command?: string;
}

function EditCredentialLink({ test }: { test: KubesoloTest }) {
  return (
    <Link
      to="portainer.settings.sharedcredentials.credential"
      params={{ id: test.credentialID }}
      className="hyperlink"
      data-cy="kubesolo-edit-credential-link"
    >
      Edit credential
    </Link>
  );
}

/** A file of Portainer's own, so it never replaces the admin's. sudo skips files with a dot in their name. */
function sudoersFile(user = '') {
  return `/etc/sudoers.d/portainer-${user.replace(dotPattern, '_')}`;
}

function Command({ command }: { command: string }) {
  return (
    <span className="mt-1 flex items-center gap-2">
      <code className="break-all">{command}</code>
      <CopyButton
        copyText={command}
        data-cy="kubesolo-copy-fix-command"
        aria-label="Copy command"
      >
        Copy
      </CopyButton>
    </span>
  );
}
